import { message } from 'antd';
import dayjs from 'dayjs';

export const slugify = (text) => text
  .toString()
  .toLowerCase()
  .replace(/\s+/g, '-') // Replace spaces with -
  .replace(/[^\w\-]+/g, '') // Remove all non-word chars
  .replace(/\-\-+/g, '-') // Replace multiple - with single -
  .replace(/^-+/, '') // Trim - from start of text
  .replace(/-+$/, '');

export const isEmpty = (obj) => {
  if (typeof obj !== typeof true && typeof obj !== typeof 0 && !obj) return true;
  if (obj.length === 0) return true;
  if (obj === '') return true;
  if (typeof obj === typeof {} && obj.hasOwnProperty('id')) return obj.id.value ? obj.id.value === '' : obj.id === '';
  return false;
};

export const isMobileSize = () => (!(window.innerWidth > 900));

export const immutableMove = (arr, from, to) => arr.reduce((prev, current, idx, self) => {
  if (from === to) {
    prev.push(current);
  }
  if (idx === from) {
    return prev;
  }
  if (from < to) {
    prev.push(current);
  }
  if (idx === to) {
    prev.push(self[from]);
  }
  if (from > to) {
    prev.push(current);
  }
  return prev;
}, []);

export const checkRegex = (str, regex) => {
  if (!regex) return true;
  const patt = new RegExp(regex);
  const res = patt.exec(str);
  if (res === null) return false;
  let ok = false;
  for (let i = 0; i < getData(res)?.length; i++) {
    if (getData(res)[i] === str) ok = true;
  }
  return ok;
};

export const subtractArrays = (a, b) => a.filter((x) => !b.includes(x));

export const insertDecimal = (num) => {
  if (!num) return 0;
  // return (Math.round(num * 100) / 100).toFixed(2);
  return Number(num).toFixed(2);
};

export const askUserPermission = async () => await Notification.requestPermission();

export const formatPhoneNumber = (phone) => {
  if (!phone) {
    return '';
  }
  phone = `${phone.startsWith('+') ? '' : '+'}${phone}`;
  const regex = /([+]\d{1,4})(\d{3})(\d{3})(\d{4})/;
  const match = phone.match(regex);

  if (!match) {
    return phone;
  }

  const [, prefix, middle1, middle2, last] = match;

  return `${prefix} (${middle1}) ${middle2}-${last}`;
};

/** Digits only for react-phone-input-2 controlled values (strips leading +). */
export const phoneDigitsForInput = (phone) => {
  if (!phone) return '';
  return String(phone).replace(/^\+/, '').replace(/\D/g, '');
};

/**
 * Normalize to E.164 (+digits). Empty / country-code-only values become ''.
 * react-phone-input-2 typically returns digits without '+'.
 */
export const toE164Phone = (phone) => {
  if (phone == null || phone === '') return '';
  const digits = String(phone).replace(/\D/g, '');
  // Country code alone (e.g. "1") is treated as empty for optional fields.
  if (!digits || digits.length < 8) return '';
  if (digits.length > 15) return '';
  return `+${digits}`;
};

/** Ant Design Form rule: optional, but if filled must be a valid E.164-ish number. */
export const optionalPhoneRule = {
  validator: (_, value) => {
    if (value == null || value === '') return Promise.resolve();
    const digits = String(value).replace(/\D/g, '');
    if (!digits || digits.length < 8) return Promise.resolve(); // treat as empty
    if (digits.length > 15) {
      return Promise.reject(new Error('Enter a valid phone number with country code'));
    }
    if (digits.length < 10) {
      return Promise.reject(new Error('Enter a complete phone number'));
    }
    return Promise.resolve();
  },
};

/** Ant Design Form rule: required valid phone. */
export const requiredPhoneRule = {
  validator: (_, value) => {
    const digits = String(value || '').replace(/\D/g, '');
    if (!digits || digits.length < 10 || digits.length > 15) {
      return Promise.reject(new Error('Enter a valid phone number with country code'));
    }
    return Promise.resolve();
  },
};

/** Normalize the POS / Omni support number to its required 10-digit NANP form. */
export const toOmniSupportPhone = (phone) => {
  const text = String(phone || '').trim();
  if (!text || !/^[\d+\-().\s]+$/.test(text)) return '';
  let digits = text.replace(/\D/g, '');
  if (digits.length === 11 && digits.startsWith('1')) digits = digits.slice(1);
  return digits.length === 10 ? digits : '';
};

export const omniSupportPhoneRule = {
  validator: (_, value) => (
    toOmniSupportPhone(value)
      ? Promise.resolve()
      : Promise.reject(new Error('Enter a valid 10-digit US or Canada phone number'))
  ),
};

export const generateURLWithParams = (url, params) => {
  if (!params) return url;
  let newURL = url;
  Object.keys(params).filter((key) => params[key]).forEach((key, index) => {
    const sign = index === 0 ? '?' : '&';
    newURL += `${sign}${key}=${params[key]}`;
  });
  return newURL;
};

export const getData = (res) =>
  /** Get the data part of response */
  res?.data || res;
export const errorHandling = (res, retryCallback) => {
  /** Following the new pattern of error responses from server,
  we should show the messages, errors and details.
  Accepts axios err.response (has .data, .status) - pass err?.response || err from catch. */
  if (!res) return;
  if (res?.data?.status === 'success') {
    if (res?.data?.message) {
      /** Success messages */
      message.success(res?.data?.message);
    }
  }

  if (res?.data?.status === 'failed') {
    /** Error messages */
    if (res?.data?.detail) {
      /** Detail */
      const key = `error-${Date.now()}`;
      const detailContent = typeof res.data.detail === 'string' ? res.data.detail : 'Request failed';
      message.error({
        content: detailContent,
        key,
        duration: 0,
        onClick: () => {
          message.destroy(key);
        },
        ...(retryCallback && {
          btn: (
            <a
              onClick={() => {
                message.destroy(key);
                retryCallback();
              }}
            >
              Retry
            </a>
          ),
        }),
      });
    }
    if (res?.data?.errors) {
      const rawErrors = res?.data?.errors;
      const errorList = Array.isArray(rawErrors) ? rawErrors : Object.values(rawErrors || {});
      if (errorList?.length > 0) {
        for (let i = 0; i < errorList.length; i++) {
          const item = errorList[i];
          const textError = typeof item === 'string' ? item
            : typeof item === 'object' && item !== null
              ? Object.entries(item).map(([k, v]) => `${k}: ${Array.isArray(v) ? v.join(', ') : v}`).join('; ')
              : String(item);
          const key = `error-${Date.now()}-${i}`;
          message.error({
            content: textError,
            key,
            duration: 0,
            onClick: () => {
              message.destroy(key);
            },
            ...(retryCallback && {
              btn: (
                <a
                  onClick={() => {
                    message.destroy(key);
                    retryCallback();
                  }}
                >
                  Retry
                </a>
              ),
            }),
          });
        }
      }
    }
  }
  if (res?.status === 500) {
    const key = `error-500-${Date.now()}`;
    message.error({
      content: 'Something went wrong!',
      key,
      duration: 0,
      onClick: () => {
        message.destroy(key);
      },
      ...(retryCallback && {
        btn: (
          <a
            onClick={() => {
              message.destroy(key);
              retryCallback();
            }}
          >
            Retry
          </a>
        ),
      }),
    });
  }
  // Fallback for 4xx/5xx without custom format (e.g. Django REST validation errors)
  if (res?.status >= 400 && res?.status !== 500 && !res?.data?.status) {
    const detail = res?.data?.detail ?? res?.data?.message;
    const errors = res?.data?.errors;
    let content = typeof detail === 'string' ? detail : null;
    if (!content && errors) {
      const list = Array.isArray(errors) ? errors : Object.values(errors);
      content = list.map((e) => (typeof e === 'string' ? e : (typeof e === 'object' && e
        ? Object.entries(e).map(([k, v]) => `${k}: ${Array.isArray(v) ? v.join(', ') : v}`).join('; ')
        : String(e)))).filter(Boolean).join('; ') || null;
    }
    if (!content) content = res?.status === 404 ? 'Not found' : 'Request failed';
    message.error({ content: String(content), key: `error-${Date.now()}`, duration: 4 });
  }
};

export const priceTransformer = (value, hideSign) => {
  const sign = hideSign ? '' : '$';
  const num = Number(value) || 0;
  const price = (Math.round(num * 100) / 100).toFixed(2);
  return `${sign}${price}`;
};

export function formatAmount(amount, { isCent, hideSign } = {}) {
  const value = isCent ? amount / 100 : amount;
  return `${hideSign ? '' : '$'}${(
    Math.round((value || 0) * 100) / 100
  ).toFixed(2)}`;
}

export const generateQuery = (queryArray) => {
  /** Get an array of queries and generate a query string */
  const validatedQueryArray = [];
  for (let i = 0; i < queryArray.length; i++) {
    const element = queryArray[i];
    if (element) {
      validatedQueryArray.push(element);
    }
  }

  return validatedQueryArray?.length ? `?${validatedQueryArray.join('&')}` : '';
};

export const calcSomeDaysAgoDate = (numberOfDaysBefore) =>
  /** Calculte numberOfDaysBefore days before now  */
  formatDate(new Date().setDate(new Date().getDate() - numberOfDaysBefore));
export const formatDate = (date) =>
  /** Formate date to YYYY-MM-DD */
  (date ? dayjs(date).format('YYYY-MM-DD') : null);
export const getQueryStringValue = (key) => new URLSearchParams(window.location.search).get(key);

/**
 * Capitalizes merchant type for display
 * Handles special cases like "non-partner" -> "Non-Partner"
 * @param {string} merchantType - The merchant type value (e.g., "catering", "partner", "omni", "non-partner")
 * @returns {string} Capitalized merchant type
 */
export const capitalizeMerchantType = (merchantType) => {
  if (!merchantType) return '';

  if (merchantType.toLowerCase() === 'omni') {
    return 'Auto Merchant';
  }

  // Handle hyphenated types like "non-partner"
  if (merchantType.includes('-')) {
    return merchantType
      .split('-')
      .map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
      .join('-');
  }
  
  // Capitalize first letter, lowercase the rest
  return merchantType.charAt(0).toUpperCase() + merchantType.slice(1).toLowerCase();
};
